import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  productsApi,
  publishApi,
  sellersApi,
  schedulesApi,
  type Product,
  type ProductCreate,
  type ProductUpdate,
  type PublishOneResult,
  type SellerListItem,
  type Schedule,
  type ConsistItem,
  ApiError,
} from '../lib/api';
import { useAuth } from '../lib/auth';

// Suggested property keys that map cleanly to Flowwow's flat schema.
// User can also add free-form keys.
const PROPERTY_HINTS: { key: string; label: string }[] = [
  { key: 'height',          label: 'Высота, см' },
  { key: 'width',           label: 'Ширина, см' },
  { key: 'manufacturerName', label: 'Производитель' },
  { key: 'regionOfOrigin',  label: 'Страна' },
  { key: 'composition',     label: 'Состав / материал' },
  { key: 'flowerType',      label: 'Вид цветка' },
  { key: 'stemLength',      label: 'Длина стебля' },
];

type PropRow = { key: string; value: string };

function propsObjectToRows(obj: Record<string, unknown>): PropRow[] {
  return Object.entries(obj).map(([k, v]) => ({ key: k, value: String(v ?? '') }));
}
function propsRowsToObject(rows: PropRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = r.key.trim();
    if (!k) continue;
    out[k] = r.value;
  }
  return out;
}

export default function ProductEdit() {
  const { id: idStr } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isNew = !idStr || idStr === 'new';
  const id = isNew ? null : Number(idStr);

  const [sellers, setSellers]     = useState<SellerListItem[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  const [sellerId, setSellerId] = useState<number | null>(user?.role === 'admin' ? null : (user?.id ?? null));
  const [scheduleId, setScheduleId] = useState<number | null>(null);

  // form fields
  const [externalOfferId, setExternalOfferId] = useState('');
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice]             = useState<string>('0');
  const [categoryId, setCategoryId]   = useState<string>('450');     // 'Цветы пачками' as default
  const [subCategoryId, setSubCategoryId] = useState<string>('');
  const [propsRows, setPropsRows] = useState<PropRow[]>([]);
  const [consist, setConsist]     = useState<ConsistItem[]>([]);
  const [isActive, setIsActive]   = useState(true);
  const [images, setImages]       = useState<string[]>([]);
  const [product, setProduct]     = useState<Product | null>(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Publish state
  const [publishing, setPublishing] = useState<'dry' | 'real' | null>(null);
  const [publishResult, setPublishResult] = useState<PublishOneResult | null>(null);
  const [publishErr, setPublishErr] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initial loads
  useEffect(() => {
    if (user?.role === 'admin') sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
  }, [user]);

  useEffect(() => {
    schedulesApi.list(sellerId ?? undefined).then((r) => setSchedules(r.schedules)).catch(() => setSchedules([]));
  }, [sellerId]);

  useEffect(() => {
    if (isNew || !id) return;
    productsApi.get(id)
      .then(({ product: p }) => {
        setProduct(p);
        setSellerId(p.seller_id);
        setScheduleId(p.schedule_group_id);
        setExternalOfferId(p.external_offer_id);
        setName(p.name);
        setDescription(p.description ?? '');
        setPrice(String(p.price_rub));
        setCategoryId(p.category_id ? String(p.category_id) : '');
        setSubCategoryId(p.sub_category_id ? String(p.sub_category_id) : '');
        setPropsRows(propsObjectToRows(p.properties));
        setConsist(p.consist);
        setIsActive(p.is_active);
        setImages(p.images);
      })
      .catch((e) => setErr(String(e?.message ?? e)));
  }, [id, isNew]);

  function commonBody(): Omit<ProductCreate, 'seller_id'> {
    return {
      schedule_group_id: scheduleId,
      external_offer_id: externalOfferId.trim(),
      name: name.trim(),
      description: description.trim() || null,
      price_rub: Number(price) || 0,
      category_id: categoryId ? Number(categoryId) : null,
      sub_category_id: subCategoryId ? Number(subCategoryId) : null,
      properties: propsRowsToObject(propsRows),
      consist,
      is_active: isActive,
    };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setSavedMsg(null);
    try {
      if (isNew) {
        if (!sellerId) throw new Error('Не выбран продавец');
        const body: ProductCreate = { seller_id: sellerId, ...commonBody() };
        const r = await productsApi.create(body);
        nav(`/products/${r.product.id}`, { replace: true });
      } else if (id) {
        const r = await productsApi.update(id, commonBody() as ProductUpdate);
        setProduct(r.product);
        setSavedMsg('Сохранено');
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setErr('Артикул уже занят у этого продавца');
      else setErr(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm('Удалить товар? Картинки тоже будут удалены.')) return;
    await productsApi.remove(id);
    nav('/products', { replace: true });
  }

  async function onFilesPicked(files: FileList | null) {
    if (!files || !files.length || !id) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await productsApi.uploadImages(id, Array.from(files));
      setImages(r.images);
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function onDeleteImage(idx: number) {
    if (!id) return;
    const r = await productsApi.deleteImage(id, idx);
    setImages(r.images);
  }

  async function moveImage(idx: number, dir: -1 | 1) {
    if (!id) return;
    const target = idx + dir;
    if (target < 0 || target >= images.length) return;
    const order = images.map((_, i) => i);
    [order[idx], order[target]] = [order[target], order[idx]];
    const r = await productsApi.reorderImages(id, order);
    setImages(r.images);
  }

  async function onPublish(mode: 'dry' | 'real') {
    if (!id) return;
    setPublishing(mode);
    setPublishErr(null);
    setPublishResult(null);
    try {
      const r = await publishApi.one(id, mode === 'dry');
      setPublishResult(r);
      // Refresh product to show updated last_pushed_at / status
      if (mode === 'real') {
        productsApi.get(id).then(({ product: p }) => setProduct(p)).catch(() => { /* noop */ });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401 && (e.body as { error?: string })?.error === 'flowwow_unauthorized') {
          setPublishErr('Flowwow вернул 401 — проверь токен продавца.');
        } else {
          setPublishErr(`Ошибка ${e.status}: ${e.message}`);
        }
      } else {
        setPublishErr(String((e as Error)?.message ?? e));
      }
    } finally {
      setPublishing(null);
    }
  }

  function addPropRow() {
    setPropsRows([...propsRows, { key: '', value: '' }]);
  }
  function updatePropRow(idx: number, patch: Partial<PropRow>) {
    setPropsRows(propsRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removePropRow(idx: number) {
    setPropsRows(propsRows.filter((_, i) => i !== idx));
  }

  function addConsistRow() {
    setConsist([...consist, { name: '', qty: 1, unit: 'шт' }]);
  }
  function updateConsistRow(idx: number, patch: Partial<ConsistItem>) {
    setConsist(consist.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeConsistRow(idx: number) {
    setConsist(consist.filter((_, i) => i !== idx));
  }

  return (
    <div className="container">
      <div className="page-head">
        <h2>{isNew ? 'Новый товар' : product?.name || `Товар #${id}`}</h2>
        <div className="row">
          <Link to="/products" className="btn">← К списку</Link>
          {!isNew && <button className="btn" onClick={onDelete} type="button">Удалить</button>}
        </div>
      </div>

      <form className="card form" onSubmit={onSubmit}>
        {isNew && user?.role === 'admin' && (
          <label>
            <span>Продавец *</span>
            <select
              value={sellerId ?? ''}
              onChange={(e) => setSellerId(Number(e.target.value) || null)}
              required
            >
              <option value="">— выберите —</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || s.login}</option>
              ))}
            </select>
          </label>
        )}

        <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
          <label style={{ flex: 1 }}>
            <span>Артикул (offerId) *</span>
            <input
              value={externalOfferId}
              onChange={(e) => setExternalOfferId(e.target.value)}
              placeholder="например: Fla00035c"
              required
              disabled={!isNew}
            />
          </label>
          <label style={{ flex: 1 }}>
            <span>Цена, ₽ *</span>
            <input
              type="number"
              min={0}
              step={1}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </label>
        </div>

        <label>
          <span>Название *</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <label>
          <span>Описание</span>
          <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
          <label style={{ flex: 1 }}>
            <span>Расписание</span>
            <select value={scheduleId ?? ''} onChange={(e) => setScheduleId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— без расписания —</option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (по умолч.)' : ''}</option>
              ))}
            </select>
          </label>
          <label style={{ flex: 1 }}>
            <span>categoryId Flowwow</span>
            <input
              type="number"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              placeholder="450 — Цветы пачками"
            />
          </label>
          <label style={{ flex: 1 }}>
            <span>subCategoryId</span>
            <input
              type="number"
              value={subCategoryId}
              onChange={(e) => setSubCategoryId(e.target.value)}
              placeholder="(если задаётся)"
            />
          </label>
        </div>

        <label className="row">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span>Активен (публикуется и обновляется воркером)</span>
        </label>

        <fieldset className="fieldset">
          <legend>Характеристики</legend>
          {propsRows.length === 0 && (
            <p className="muted">Пока пусто. Кнопки ниже подставляют типичные ключи.</p>
          )}
          {propsRows.map((row, idx) => (
            <div key={idx} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <input
                style={{ flex: 1 }}
                value={row.key}
                onChange={(e) => updatePropRow(idx, { key: e.target.value })}
                placeholder="ключ (height, width, manufacturerName, …)"
              />
              <input
                style={{ flex: 2 }}
                value={row.value}
                onChange={(e) => updatePropRow(idx, { value: e.target.value })}
                placeholder="значение"
              />
              <button type="button" className="btn" onClick={() => removePropRow(idx)}>✕</button>
            </div>
          ))}
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {PROPERTY_HINTS.map((h) => (
              <button
                key={h.key}
                type="button"
                className="btn"
                onClick={() => {
                  if (propsRows.some((r) => r.key === h.key)) return;
                  setPropsRows([...propsRows, { key: h.key, value: '' }]);
                }}
              >
                + {h.label}
              </button>
            ))}
            <button type="button" className="btn" onClick={addPropRow}>+ свой ключ</button>
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Состав (consist)</legend>
          {consist.length === 0 && <p className="muted">Пусто. Кнопка ниже добавляет строку.</p>}
          {consist.map((row, idx) => (
            <div key={idx} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <input
                style={{ flex: 2 }}
                value={row.name}
                onChange={(e) => updateConsistRow(idx, { name: e.target.value })}
                placeholder="название (Роза одноголовая Абигейл)"
              />
              <input
                style={{ width: 80 }}
                type="number"
                min={1}
                value={row.qty}
                onChange={(e) => updateConsistRow(idx, { qty: Number(e.target.value) || 1 })}
                placeholder="кол-во"
              />
              <input
                style={{ width: 70 }}
                value={row.unit}
                onChange={(e) => updateConsistRow(idx, { unit: e.target.value })}
                placeholder="ед."
              />
              <button type="button" className="btn" onClick={() => removeConsistRow(idx)}>✕</button>
            </div>
          ))}
          <button type="button" className="btn" onClick={addConsistRow}>+ компонент</button>
        </fieldset>

        {err && <div className="error">{err}</div>}

        <div className="row">
          <button className="btn primary" disabled={busy} type="submit">
            {busy ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>
      </form>

      {!isNew && (
        <div className="card form" style={{ marginTop: 20 }}>
          <h3>Картинки</h3>
          <div className="row">
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={(e) => onFilesPicked(e.target.files)}
              disabled={busy}
            />
            <span className="muted">JPG/PNG/WebP. Сохраним в WebP, до 2000px. До 12 за раз.</span>
          </div>

          {images.length === 0 ? (
            <p className="muted">Картинок пока нет.</p>
          ) : (
            <div className="image-grid">
              {images.map((url, idx) => (
                <div key={url} className="image-tile">
                  <img src={url} alt="" />
                  <div className="image-tile-tools">
                    <button type="button" className="btn" onClick={() => moveImage(idx, -1)} disabled={idx === 0}>←</button>
                    <span className="muted">{idx + 1}</span>
                    <button type="button" className="btn" onClick={() => moveImage(idx, 1)} disabled={idx === images.length - 1}>→</button>
                    <button type="button" className="btn" onClick={() => onDeleteImage(idx)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!isNew && product && (
        <div className="card form" style={{ marginTop: 20 }}>
          <h3>Публикация в Flowwow</h3>
          <p className="muted">
            «Превью» собирает payload без отправки. «Опубликовать сейчас» делает реальный <code>products/{product.flowwow_product_id ? 'update' : 'create'}</code> с токеном продавца.
          </p>
          <div className="row">
            <button type="button" className="btn" disabled={publishing !== null} onClick={() => onPublish('dry')}>
              {publishing === 'dry' ? 'Считаем…' : 'Превью payload'}
            </button>
            <button type="button" className="btn primary" disabled={publishing !== null} onClick={() => onPublish('real')}>
              {publishing === 'real' ? 'Публикуем…' : 'Опубликовать сейчас'}
            </button>
          </div>
          {publishErr && <div className="error" style={{ marginTop: 10 }}>{publishErr}</div>}
          {publishResult && (
            <div style={{ marginTop: 12 }}>
              <p className="muted">
                Действие: <code>{publishResult.action}</code>{' '}
                {('dry_run' in publishResult && publishResult.dry_run) ? ' (dry-run)' : ' (отправлено)'}.
                productionTime.hours = <code>{publishResult.productionTimeHours ?? '—'}</code>.
              </p>
              <pre className="raw">
                {JSON.stringify('dry_run' in publishResult ? publishResult.payload : publishResult.response, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {!isNew && product && (
        <div className="card sub" style={{ marginTop: 20 }}>
          <h4>Состояние</h4>
          <div className="kv">
            <span>Окно:</span>            <span><code>{product.current_window_state}</code></span>
            <span>Следующая отгрузка:</span> <span>{product.next_ship_dt ?? '—'}</span>
            <span>Дедлайн:</span>         <span>{product.next_deadline_dt ?? '—'}</span>
            <span>Последний productionTime:</span> <span>{product.last_production_time_hours ?? '—'}</span>
            <span>Последний push:</span>  <span>{product.last_pushed_at ?? '—'}</span>
            <span>Статус push:</span>     <span>{product.last_push_status ?? '—'}</span>
            <span>flowwow_product_id:</span> <span><code>{product.flowwow_product_id ?? '—'}</code></span>
          </div>
        </div>
      )}
    </div>
  );
}
